import React from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '@emotion/react';
import clsx from 'clsx';

// Skeleton animation
const SkeletonPulse = ({ className, ...props }) => (
  <motion.div
    className={clsx("animate-pulse bg-gray-300 dark:bg-gray-600 rounded", className)}
    initial={{ opacity: 0.6 }}
    animate={{ opacity: [0.6, 1, 0.6] }}
    transition={{ duration: 1.5, repeat: Infinity }}
    {...props}
  />
);

// Stat Card Skeleton
export const StatCardSkeleton = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  
  return (
    <motion.div
      className={clsx(
        "backdrop-blur-md shadow-lg rounded-xl p-6 border transition-colors duration-300",
        {
          "bg-gray-800 bg-opacity-50 border-gray-700": isDark,
          "bg-white border-gray-200": !isDark,
        }
      )}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.1 }}
    >
      <div className="flex items-center justify-between mb-4">
        <SkeletonPulse className="w-8 h-8 rounded-full" />
        <SkeletonPulse className="w-16 h-4 rounded" />
      </div>
      <SkeletonPulse className="w-24 h-8 rounded mb-2" />
      <SkeletonPulse className="w-32 h-4 rounded" />
    </motion.div>
  );
};

// Metric Card Skeleton
export const MetricCardSkeleton = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  
  return (
    <motion.div
      className={clsx(
        "backdrop-blur-md shadow-lg rounded-xl p-4 border transition-colors duration-300",
        {
          "bg-gray-800 bg-opacity-50 border-gray-700": isDark,
          "bg-white border-gray-200": !isDark,
        }
      )}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.1 }}
    >
      <div className="flex items-center justify-between mb-3">
        <SkeletonPulse className="w-6 h-6 rounded" />
        <SkeletonPulse className="w-12 h-3 rounded" />
      </div>
      <SkeletonPulse className="w-20 h-5 rounded mb-2" />
      <SkeletonPulse className="w-16 h-3 rounded" />
    </motion.div>
  );
};

// Chart Skeleton
export const ChartSkeleton = ({ height = 300 }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  
  return (
    <motion.div
      className={clsx(
        "backdrop-blur-md shadow-lg rounded-xl p-6 border transition-colors duration-300",
        {
          "bg-gray-800 bg-opacity-50 border-gray-700": isDark,
          "bg-white border-gray-200": !isDark,
        }
      )}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.1 }}
      style={{ height }}
    >
      <div className="flex justify-between items-center mb-4">
        <SkeletonPulse className="w-32 h-6 rounded" />
        <SkeletonPulse className="w-24 h-4 rounded" />
      </div>
      <div className="flex items-end justify-between h-48 space-x-2">
        {[...Array(7)].map((_, i) => (
          <SkeletonPulse
            key={i}
            className="flex-1 rounded-t"
            style={{ height: `${Math.random() * 60 + 20}%` }}
          />
        ))}
      </div>
    </motion.div>
  );
};

// Table Skeleton
export const TableSkeleton = ({ rows = 5, columns = 6 }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  
  return (
    <motion.div
      className={clsx(
        "backdrop-blur-md shadow-lg rounded-xl p-6 border transition-colors duration-300",
        {
          "bg-gray-800 bg-opacity-50 border-gray-700": isDark,
          "bg-white border-gray-200": !isDark,
        }
      )}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.1 }}
    >
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <SkeletonPulse className="w-32 h-6 rounded" />
        <SkeletonPulse className="w-24 h-8 rounded" />
      </div>
      
      {/* Table */}
      <div className="overflow-hidden rounded-lg">
        {/* Table Header */}
        <div className={clsx("grid gap-4 p-4", {
          "bg-gray-900": isDark,
          "bg-gray-100": !isDark,
        })} style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}>
          {[...Array(columns)].map((_, i) => (
            <SkeletonPulse key={i} className="h-4 rounded" />
          ))}
        </div>
        
        {/* Table Rows */}
        {[...Array(rows)].map((_, rowIndex) => (
          <div
            key={rowIndex}
            className={clsx("grid gap-4 p-4 border-b", {
              "border-gray-700": isDark,
              "border-gray-200": !isDark,
            })} style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}
          >
            {[...Array(columns)].map((_, colIndex) => (
              <SkeletonPulse key={colIndex} className="h-4 rounded" />
            ))}
          </div>
        ))}
      </div>
      
      {/* Pagination */}
      <div className="flex justify-between items-center mt-4">
        <SkeletonPulse className="w-32 h-4 rounded" />
        <div className="flex space-x-2">
          {[...Array(3)].map((_, i) => (
            <SkeletonPulse key={i} className="w-8 h-8 rounded" />
          ))}
        </div>
      </div>
    </motion.div>
  );
};

// Pie Chart Skeleton
export const PieChartSkeleton = ({ height = 300 }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  
  return (
    <motion.div
      className={clsx(
        "backdrop-blur-md shadow-lg rounded-xl p-6 border transition-colors duration-300",
        {
          "bg-gray-800 bg-opacity-50 border-gray-700": isDark,
          "bg-white border-gray-200": !isDark,
        }
      )}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.1 }}
      style={{ height }}
    >
      <div className="flex justify-between items-center mb-4">
        <SkeletonPulse className="w-32 h-6 rounded" />
        <SkeletonPulse className="w-24 h-4 rounded" />
      </div>
      <div className="flex items-center justify-center h-48">
        <div className="relative">
          {/* Pie chart circle */}
          <SkeletonPulse className="w-44 h-44 rounded-full" />
          {/* Legend items */}
          <div className="mt-4 space-y-2">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="flex items-center space-x-2">
                <SkeletonPulse className="w-3 h-3 rounded" />
                <SkeletonPulse className="w-20 h-3 rounded" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// Dashboard Skeleton
export const DashboardSkeleton = () => {
  return (
    <div className="flex-1 relative z-10 overflow-auto">
      <div className="max-w-7xl mx-auto py-4 px-4 lg:px-8">
        <div className="flex justify-end">
          <SkeletonPulse className="w-48 h-10 rounded" />
        </div>
      </div>
      <main className="max-w-7xl mx-auto py-6 px-4 lg:px-8">
        {/* Stat Cards */}
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 mb-8 mt-5">
          <StatCardSkeleton />
          <StatCardSkeleton />
          <StatCardSkeleton />
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
          {/* Left: Metric Cards */}
          <div className="md:col-span-1 space-y-4">
            {[...Array(8)].map((_, i) => (
              <MetricCardSkeleton key={i} />
            ))}
          </div>
          
          {/* Right: Charts - Fixed order to match actual layout */}
          <div className="md:col-span-2 flex flex-col gap-8">
            <PieChartSkeleton title="Department-wise Call Distribution" />
            <ChartSkeleton title="Hourly Performance" />
            <ChartSkeleton title="Weekly Trends" />
            <PieChartSkeleton title="Customer Sentiment Analysis" />
          </div>
        </div>

        {/* Bottom Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-1 gap-8 mb-8">
          <ChartSkeleton title="Performance Metrics" height={400} />
          <TableSkeleton title="Intents Table" rows={5} columns={6} />
        </div>
      </main>
    </div>
  );
};

// Call Logs/Analytics Skeleton
export const CallTableSkeleton = ({ title = "Call Data" }) => {
  return (
    <motion.div
      className="backdrop-blur-md shadow-lg rounded-xl p-4 sm:p-8 border w-full mx-auto my-6"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2 }}
      style={{ minHeight: '80vh' }}
    >
      {/* Top Controls Row */}
      <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
        <div className="w-full sm:w-auto flex-1 flex justify-start">
          <SkeletonPulse className="w-48 h-10 rounded" />
        </div>
        <div className="w-full sm:w-auto flex-1 flex justify-end items-center gap-2">
          <div className="flex items-center gap-1">
            {[...Array(5)].map((_, i) => (
              <SkeletonPulse key={i} className="w-8 h-8 rounded" />
            ))}
          </div>
          <SkeletonPulse className="w-8 h-8 rounded" />
        </div>
      </div>

      {/* Table */}
      <TableSkeleton rows={10} columns={10} />
    </motion.div>
  );
};

export default {
  StatCardSkeleton,
  MetricCardSkeleton,
  ChartSkeleton,
  TableSkeleton,
  DashboardSkeleton,
  CallTableSkeleton
}; 